import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeJsonApiCall } from '../utils/customerUtils'
import ModalShell from './ui/ModalShell'
import ModalFooter from './ui/ModalFooter'
import { buttonVariants } from './ui/buttonVariants'

interface FollowUpModalProps {
  isOpen: boolean
  onClose: () => void
  leadId: number
  leadName: string
  onSuccess?: () => void
}

interface FollowUpForm {
  days: number
  description: string
  priority: 'low' | 'medium' | 'high'
  type: 'call' | 'email' | 'meeting' | 'general'
  notes: string
}

const PRESET_DAYS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
  { label: 'Next month', days: 30 },
]

const FOLLOW_UP_TYPES = [
  { value: 'call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'meeting', label: 'Meeting', icon: '🤝' },
  { value: 'general', label: 'General Follow-up', icon: '📋' },
]

const DEFAULT_FORM: FollowUpForm = { days: 1, description: '', priority: 'medium', type: 'call', notes: '' }

const FollowUpModal: React.FC<FollowUpModalProps> = ({ isOpen, onClose, leadId, leadName, onSuccess }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<FollowUpForm>(DEFAULT_FORM)

  const formatDueDate = () => {
    const d = new Date()
    d.setDate(d.getDate() + form.days)
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const dueDate = new Date()
      dueDate.setDate(dueDate.getDate() + form.days)

      const taskData = {
        description: form.description || `${FOLLOW_UP_TYPES.find(ft => ft.value === form.type)?.label} with ${leadName}`,
        due_date: dueDate.toISOString().split('T')[0],
        priority: form.priority,
        status: 'pending',
        lead_id: leadId,
        notes: form.notes,
        task_type: 'follow_up',
        follow_up_type: form.type,
      }

      const response = await makeJsonApiCall('/api/tasks', 'POST', taskData)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to create follow-up')
      }

      onClose()
      onSuccess?.()
      setForm(DEFAULT_FORM)
    } catch (err: any) {
      setError(err.message || 'Failed to create follow-up')
    } finally {
      setLoading(false)
    }
  }

  const set = <K extends keyof FollowUpForm>(key: K, value: FollowUpForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title="Schedule Follow-up" subtitle={`Following up with ${leadName}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Follow-up Type */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Follow-up Type</p>
          <div className="grid grid-cols-2 gap-2">
            {FOLLOW_UP_TYPES.map(ft => (
              <button
                key={ft.value}
                type="button"
                onClick={() => set('type', ft.value as FollowUpForm['type'])}
                className={`p-3 border-2 rounded-lg text-left transition-all ${
                  form.type === ft.value
                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-900 dark:text-indigo-100'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <span className="text-lg mr-2">{ft.icon}</span>
                <span className="text-sm font-medium">{ft.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* When */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">When to follow up</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {PRESET_DAYS.map(p => (
              <button
                key={p.days}
                type="button"
                onClick={() => set('days', p.days)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  form.days === p.days
                    ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Custom:</span>
            <input
              type="number"
              min="1"
              max="365"
              value={form.days}
              onChange={(e) => set('days', parseInt(e.target.value) || 1)}
              className="w-20 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-500">days</span>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Due: {formatDueDate()}</p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Task Description <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder={`${FOLLOW_UP_TYPES.find(ft => ft.value === form.type)?.label} with ${leadName}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
          <select
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as FollowUpForm['priority'])}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="low">Low Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="high">High Priority</option>
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            placeholder="Any additional context..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
          />
        </div>

        <ModalFooter>
          <button type="button" onClick={onClose} disabled={loading} className={buttonVariants.secondary}>
            Cancel
          </button>
          <button type="submit" disabled={loading} className={buttonVariants.primary}>
            {loading ? 'Creating…' : 'Create Follow-up'}
          </button>
        </ModalFooter>
      </form>
    </ModalShell>
  )
}

export default FollowUpModal
